import { Grid } from './grid';
import { cellKey, manhattan, type Cell } from './types';

interface OpenNode {
  cell: Cell;
  g: number;
  f: number;
}

export function findPath(
  grid: Grid,
  start: Cell,
  goal: Cell,
  isBlocked: (cell: Cell) => boolean,
  maxVisited = 512,
): Cell[] {
  if (cellKey(start) === cellKey(goal)) return [];
  const open: OpenNode[] = [{ cell: start, g: 0, f: manhattan(start, goal) }];
  const cameFrom = new Map<string, Cell>();
  const costs = new Map<string, number>([[cellKey(start), 0]]);
  let visited = 0;

  while (open.length > 0 && visited < maxVisited) {
    open.sort((a, b) => a.f - b.f || a.g - b.g || a.cell.y - b.cell.y || a.cell.x - b.cell.x);
    const current = open.shift();
    if (!current) break;
    visited += 1;
    if (cellKey(current.cell) === cellKey(goal)) return rebuild(cameFrom, start, goal);

    for (const neighbor of grid.neighbors(current.cell, (cell) => isBlocked(cell) && cellKey(cell) !== cellKey(goal))) {
      const nextCost = current.g + 1;
      const neighborKey = cellKey(neighbor);
      if (nextCost >= (costs.get(neighborKey) ?? Number.POSITIVE_INFINITY)) continue;
      costs.set(neighborKey, nextCost);
      cameFrom.set(neighborKey, current.cell);
      open.push({ cell: neighbor, g: nextCost, f: nextCost + manhattan(neighbor, goal) });
    }
  }
  return [];
}

function rebuild(cameFrom: Map<string, Cell>, start: Cell, goal: Cell): Cell[] {
  const path: Cell[] = [];
  let cursor: Cell = goal;
  const startKey = cellKey(start);
  while (cellKey(cursor) !== startKey) {
    path.push(cursor);
    const previous = cameFrom.get(cellKey(cursor));
    if (!previous) return [];
    cursor = previous;
  }
  return path.reverse();
}
